import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Paper, Button, Tabs, Tab, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Chip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Grid, CircularProgress, Alert, Tooltip, FormControl, InputLabel,
  Select, MenuItem, Autocomplete, Divider, Paper as MuiPaper
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  PictureAsPdf as PdfIcon,
  Code as XmlIcon,
  Cancel as CancelIcon,
  Visibility as ViewIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

export default function FiscalDocuments() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openEmit, setOpenEmit] = useState(false);
  const [emitType, setEmitType] = useState('nfe');
  const [emitting, setEmitting] = useState(false);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [cancelJustification, setCancelJustification] = useState('');
  const [cancelDoc, setCancelDoc] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [customers, setCustomers] = useState([]);
  const lastRecipientCepRequestedRef = useRef('');
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [filters, setFilters] = useState({ type: '', status: '' });
  const [fiscalAlert, setFiscalAlert] = useState(null);

  const [formData, setFormData] = useState({
    recipient_cpf_cnpj: '',
    recipient_name: '',
    recipient_ie: '',
    recipient_address: {
      logradouro: '',
      numero: '',
      bairro: '',
      codigoMunicipio: '',
      nomeMunicipio: '',
      uf: '',
      cep: '',
    },
    items: [{ codigo: '', descricao: '', quantidade: 1, valor_unitario: 0, valor_total: 0 }],
    total_value: 0,
    environment: 'producao',
    payment_type: '01'
  });

  const getApiErrorMessage = (error, fallback) => {
    const backendError = error?.response?.data?.error;
    const backendMessage = error?.response?.data?.message;
    const validationErrors = error?.response?.data?.errors;

    if (backendError) return backendError;

    if (validationErrors && typeof validationErrors === 'object') {
      const firstGroup = Object.values(validationErrors)[0];
      if (Array.isArray(firstGroup) && firstGroup.length > 0) {
        return firstGroup[0];
      }
    }

    return backendMessage || fallback;
  };

  const isCnpjNotEnabledReason = (reason) =>
    String(reason || '').toLowerCase().includes('cnpj emitente não cadastrado');

  const onlyDigits = (value) => value.replace(/\D/g, '');

  const maskCpfCnpj = (value) => {
    const digits = onlyDigits(value).slice(0, 14);

    if (digits.length <= 11) {
      return digits
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    }

    return digits
      .replace(/^(\d{2})(\d)/, '$1.$2')
      .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2');
  };

  const maskCep = (value) => {
    const digits = onlyDigits(value).slice(0, 8);
    return digits.replace(/(\d{5})(\d)/, '$1-$2');
  };

  useEffect(() => {
    fetchDocuments();
  }, [filters]);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/customers?per_page=-1`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const payload = response?.data;
      if (Array.isArray(payload)) {
        setCustomers(payload);
      } else {
        setCustomers(payload?.data || []);
      }
    } catch (_error) {
      setCustomers([]);
    }
  };

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filters.type) params.append('type', filters.type);
      if (filters.status) params.append('status', filters.status);

      const response = await axios.get(`${API}/api/fiscal/documents?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const docs = response.data.data || [];
      setDocuments(docs);

      const blockedDoc = docs.find(
        (doc) =>
          doc?.document_type === 'nfe' &&
          doc?.status === 'rejected' &&
          isCnpjNotEnabledReason(doc?.rejection_reason)
      );

      if (blockedDoc) {
        setFiscalAlert({
          type: 'cnpj_not_enabled',
          title: 'Bloqueio fiscal ativo',
          description: 'CNPJ emitente não credenciado na SEFAZ para este ambiente. Regularize o credenciamento para liberar novas emissões.',
        });
      } else {
        setFiscalAlert(null);
      }
    } catch (error) {
      toast.error('Erro ao carregar documentos');
    } finally {
      setLoading(false);
    }
  };

  const handleEmit = async () => {
    if (emitType === 'nfe') {
      const requiredAddressFields = [
        'logradouro',
        'numero',
        'bairro',
        'codigoMunicipio',
        'nomeMunicipio',
        'uf',
        'cep',
      ];

      const missing = requiredAddressFields.filter(
        (field) => !String(formData.recipient_address?.[field] ?? '').trim()
      );

      if (missing.length > 0) {
        toast.error(`Preencha o endereço do destinatário: ${missing.join(', ')}`);
        return;
      }
    }

    setEmitting(true);
    try {
      const token = localStorage.getItem('token');
      const endpoint = emitType === 'nfe' ? '/api/fiscal/nfe/emit' : '/api/fiscal/nfce/emit';

      const payloadToSend = { ...formData, environment: 'producao' };

      const response = await axios.post(`${API}${endpoint}`, payloadToSend, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const payload = response?.data || {};
      const documentStatus = payload?.document?.status;
      const rejectionReason = payload?.document?.rejection_reason || '';
      const sefazCode = String(payload?.nuvem_fiscal_response?.autorizacao?.codigo_status || '');

      if (documentStatus === 'authorized') {
        setFiscalAlert(null);
        toast.success(`${emitType.toUpperCase()} emitida com sucesso!`);
        setOpenEmit(false);
        resetForm();
      } else if (documentStatus === 'rejected') {
        toast.error(payload?.message || `${emitType.toUpperCase()} rejeitada`);

        if (
          sefazCode === '245' ||
          rejectionReason.toLowerCase().includes('cnpj emitente não cadastrado')
        ) {
          setFiscalAlert({
            type: 'cnpj_not_enabled',
            title: 'Bloqueio fiscal ativo',
            description: 'CNPJ emitente não credenciado na SEFAZ para este ambiente. Regularize o credenciamento para liberar novas emissões.',
          });
        } else {
          setFiscalAlert(null);
        }
      } else {
        toast.info(payload?.message || `${emitType.toUpperCase()} enviada para processamento.`);
      }

      fetchDocuments();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erro ao emitir documento'));
    } finally {
      setEmitting(false);
    }
  };

  const handleOpenCancelDialog = (doc) => {
    setCancelDoc(doc);
    setCancelJustification('');
    setOpenCancelDialog(true);
  };

  const handleCloseCancelDialog = () => {
    if (cancelling) return;
    setOpenCancelDialog(false);
    setCancelDoc(null);
    setCancelJustification('');
  };

  const handleCancel = async () => {
    if (!cancelDoc) return;
    const justification = cancelJustification.trim();
    if (justification.length < 15) {
      toast.error('Justificativa deve ter no mínimo 15 caracteres');
      return;
    }

    try {
      setCancelling(true);
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/fiscal/documents/${cancelDoc.id}/cancel`,
        { justification },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Documento cancelado!');
      handleCloseCancelDialog();
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao cancelar');
    } finally {
      setCancelling(false);
    }
  };

  const downloadPdf = async (doc) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/fiscal/documents/${doc.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const link = document.createElement('a');
      link.href = `data:application/pdf;base64,${response.data.pdf_base64}`;
      link.download = response.data.filename;
      link.click();
    } catch (error) {
      toast.error('Erro ao baixar PDF');
    }
  };

  const downloadXml = async (doc) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/fiscal/documents/${doc.id}/xml`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const blob = new Blob([response.data.xml], { type: 'application/xml' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = response.data.filename;
      link.click();
    } catch (error) {
      toast.error('Erro ao baixar XML');
    }
  };

  const resetForm = () => {
    setSelectedCustomerId('');
    setFormData({
      recipient_cpf_cnpj: '',
      recipient_name: '',
      recipient_ie: '',
      recipient_address: {
        logradouro: '',
        numero: '',
        bairro: '',
        codigoMunicipio: '',
        nomeMunicipio: '',
        uf: '',
        cep: '',
      },
      items: [{ codigo: '', descricao: '', quantidade: 1, valor_unitario: 0, valor_total: 0 }],
      total_value: 0,
      environment: 'producao',
      payment_type: '01'
    });
  };

  const handleSelectCustomer = (customerId) => {
    setSelectedCustomerId(customerId);

    if (!customerId) return;

    const customer = customers.find((item) => String(item.id) === String(customerId));
    if (!customer) return;

    setFormData((prev) => ({
      ...prev,
      recipient_cpf_cnpj: customer.document || '',
      recipient_name: customer.name || '',
      recipient_ie: customer.state_registration || '',
      recipient_address: {
        ...prev.recipient_address,
        logradouro: customer.address_street || '',
        numero: customer.address_number || '',
        bairro: customer.address_neighborhood || '',
        codigoMunicipio: onlyDigits(customer.address_city_code || '').slice(0, 7),
        nomeMunicipio: customer.address_city || '',
        uf: (customer.address_state || '').toUpperCase().slice(0, 2),
        cep: maskCep(customer.address_zip_code || ''),
      },
    }));
  };

  const fillRecipientAddressByCep = async (cepDigits) => {
    if (
      cepDigits.length !== 8 ||
      lastRecipientCepRequestedRef.current === cepDigits
    ) {
      return;
    }

    lastRecipientCepRequestedRef.current = cepDigits;

    try {
      const response = await axios.get(`https://viacep.com.br/ws/${cepDigits}/json/`);
      const viaCep = response.data;

      if (viaCep?.erro) return;

      setFormData((prev) => ({
        ...prev,
        recipient_address: {
          ...prev.recipient_address,
          logradouro: viaCep.logradouro || prev.recipient_address.logradouro,
          bairro: viaCep.bairro || prev.recipient_address.bairro,
          nomeMunicipio: viaCep.localidade || prev.recipient_address.nomeMunicipio,
          uf: (viaCep.uf || prev.recipient_address.uf || '').toUpperCase().slice(0, 2),
          codigoMunicipio: onlyDigits(
            viaCep.ibge || prev.recipient_address.codigoMunicipio
          ).slice(0, 7),
        },
      }));
    } catch (_error) {
      // Falha do ViaCEP não deve bloquear o preenchimento manual
    }
  };

  const updateRecipientAddress = (field, value) => {
    if (field === 'cep') {
      const cepDigits = onlyDigits(value).slice(0, 8);

      if (cepDigits.length === 8) {
        void fillRecipientAddressByCep(cepDigits);
      } else {
        lastRecipientCepRequestedRef.current = '';
      }
    }

    setFormData((prev) => ({
      ...prev,
      recipient_address: {
        ...prev.recipient_address,
        [field]: value,
      },
    }));
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { codigo: '', descricao: '', quantidade: 1, valor_unitario: 0, valor_total: 0 }]
    });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;

    if (field === 'quantidade' || field === 'valor_unitario') {
      newItems[index].valor_total = newItems[index].quantidade * newItems[index].valor_unitario;
    }

    const total = newItems.reduce((sum, item) => sum + (item.valor_total || 0), 0);
    setFormData({ ...formData, items: newItems, total_value: total });
  };

  const getStatusChip = (status) => {
    const config = {
      draft: { label: 'Rascunho', color: 'default' },
      processing: { label: 'Processando', color: 'warning' },
      authorized: { label: 'Autorizada', color: 'success' },
      rejected: { label: 'Rejeitada', color: 'error' },
      cancelled: { label: 'Cancelada', color: 'default' }
    };
    const { label, color } = config[status] || { label: status, color: 'default' };
    return <Chip label={label} color={color} size="small" />;
  };

  const recipientIsCnpj = onlyDigits(formData.recipient_cpf_cnpj || '').length === 14;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">
          <ReceiptIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Documentos Fiscais
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchDocuments}
            sx={{ mr: 1 }}
          >
            Atualizar
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => { setEmitType('nfe'); setOpenEmit(true); }}
            sx={{ mr: 1 }}
          >
            Emitir NF-e
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<AddIcon />}
            onClick={() => { setEmitType('nfce'); setOpenEmit(true); }}
          >
            Emitir NFC-e
          </Button>
        </Box>
      </Box>

      {fiscalAlert && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => navigate('/company-profile')}>
              Abrir Dados da Empresa
            </Button>
          }
        >
          <strong>{fiscalAlert.title}</strong><br />
          {fiscalAlert.description}
        </Alert>
      )}

      <Paper sx={{ mb: 3, p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Tipo</InputLabel>
              <Select
                value={filters.type}
                label="Tipo"
                onChange={(e) => setFilters({ ...filters, type: e.target.value })}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="nfe">NF-e</MenuItem>
                <MenuItem value="nfce">NFC-e</MenuItem>
                <MenuItem value="mdfe">MDF-e</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                label="Status"
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="authorized">Autorizadas</MenuItem>
                <MenuItem value="cancelled">Canceladas</MenuItem>
                <MenuItem value="rejected">Rejeitadas</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Tipo</TableCell>
              <TableCell>Número</TableCell>
              <TableCell>Destinatário</TableCell>
              <TableCell>Valor</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Data</TableCell>
              <TableCell>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : documents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Nenhum documento encontrado
                </TableCell>
              </TableRow>
            ) : (
              documents.map((doc) => (
                <TableRow key={doc.id}>
                  <TableCell>
                    <Chip label={doc.document_type?.toUpperCase()} size="small" />
                  </TableCell>
                  <TableCell>{doc.number || '-'}</TableCell>
                  <TableCell>
                    {doc.recipient_name || 'Consumidor'}<br />
                    <Typography variant="caption" color="textSecondary">
                      {doc.recipient_cpf_cnpj || ''}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(doc.total_value)}
                  </TableCell>
                  <TableCell>{getStatusChip(doc.status)}</TableCell>
                  <TableCell>
                    {new Date(doc.created_at).toLocaleDateString('pt-BR')}
                  </TableCell>
                  <TableCell>
                    {doc.status === 'authorized' && (
                      <>
                        <Tooltip title="Baixar PDF">
                          <IconButton size="small" onClick={() => downloadPdf(doc)}>
                            <PdfIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Baixar XML">
                          <IconButton size="small" onClick={() => downloadXml(doc)}>
                            <XmlIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancelar">
                          <IconButton size="small" color="error" onClick={() => handleOpenCancelDialog(doc)}>
                            <CancelIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog Emitir NF-e/NFC-e */}
      <Dialog open={openEmit} onClose={() => setOpenEmit(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          Emitir {emitType.toUpperCase()}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">Destinatário</Typography>
              <Divider sx={{ mt: 0.5, mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Autocomplete
                    sx={{ minWidth: '250px' }}
                    options={customers}
                    value={customers.find((item) => String(item.id) === String(selectedCustomerId)) || null}
                    onChange={(_event, value) => handleSelectCustomer(value?.id || '')}
                    getOptionLabel={(option) =>
                      `${option?.name || ''}${option?.document ? ` - ${option.document}` : ''}`
                    }
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Cliente Cadastrado (opcional)"
                        placeholder="Digite para buscar cliente"
                      />
                    )}
                    clearOnEscape
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="CPF/CNPJ do Destinatário"
                    value={formData.recipient_cpf_cnpj}
                    onChange={(e) => setFormData({ ...formData, recipient_cpf_cnpj: maskCpfCnpj(e.target.value) })}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Nome do Destinatário"
                    value={formData.recipient_name}
                    onChange={(e) => setFormData({ ...formData, recipient_name: e.target.value })}
                  />
                </Grid>
                {recipientIsCnpj && (
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Inscrição Estadual (IE)"
                      value={formData.recipient_ie}
                      onChange={(e) => setFormData({ ...formData, recipient_ie: e.target.value.toUpperCase() })}
                      helperText="Se contribuinte, informe IE. Se isento, informe ISENTO."
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>

            {emitType === 'nfe' && (
              <>
                <Grid item xs={12}>
                  <Alert severity="warning">
                    Para destinatário com CNPJ, o endereço completo é obrigatório pela SEFAZ.
                  </Alert>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Endereço do Destinatário</Typography>
                  <Divider sx={{ mt: 0.5, mb: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Logradouro do Destinatário"
                        value={formData.recipient_address.logradouro}
                        onChange={(e) => updateRecipientAddress('logradouro', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <TextField
                        fullWidth
                        label="Número"
                        value={formData.recipient_address.numero}
                        onChange={(e) => updateRecipientAddress('numero', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <TextField
                        fullWidth
                        label="UF"
                        inputProps={{ maxLength: 2 }}
                        value={formData.recipient_address.uf}
                        onChange={(e) => updateRecipientAddress('uf', e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 2))}
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        label="CEP"
                        value={formData.recipient_address.cep}
                        onChange={(e) => updateRecipientAddress('cep', maskCep(e.target.value))}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Bairro"
                        value={formData.recipient_address.bairro}
                        onChange={(e) => updateRecipientAddress('bairro', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Cidade"
                        value={formData.recipient_address.nomeMunicipio}
                        onChange={(e) => updateRecipientAddress('nomeMunicipio', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Cód. Município IBGE"
                        value={formData.recipient_address.codigoMunicipio}
                        onChange={(e) => updateRecipientAddress('codigoMunicipio', onlyDigits(e.target.value).slice(0, 7))}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">Itens</Typography>
              <Divider sx={{ mt: 0.5, mb: 1.5 }} />
              {formData.items.map((item, index) => (
                <Grid container spacing={1.5} key={index} sx={{ mb: 1 }}>
                  <Grid item xs={12} md={2}>
                    <TextField
                      size="small"
                      label="Código"
                      fullWidth
                      value={item.codigo}
                      onChange={(e) => updateItem(index, 'codigo', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      size="small"
                      label="Descrição"
                      fullWidth
                      value={item.descricao}
                      onChange={(e) => updateItem(index, 'descricao', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={4} md={2}>
                    <TextField
                      size="small"
                      label="Qtd"
                      type="number"
                      fullWidth
                      value={item.quantidade}
                      onChange={(e) => updateItem(index, 'quantidade', parseFloat(e.target.value) || 0)}
                    />
                  </Grid>
                  <Grid item xs={4} md={2}>
                    <TextField
                      size="small"
                      label="Valor Unit."
                      type="number"
                      fullWidth
                      value={item.valor_unitario}
                      onChange={(e) => updateItem(index, 'valor_unitario', parseFloat(e.target.value) || 0)}
                    />
                  </Grid>
                  <Grid item xs={4} md={2}>
                    <TextField
                      size="small"
                      label="Total"
                      fullWidth
                      value={item.valor_total.toFixed(2)}
                      disabled
                    />
                  </Grid>
                </Grid>
              ))}
              <Button size="small" onClick={addItem}>+ Adicionar Item</Button>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">Pagamento e Totais</Typography>
              <Divider sx={{ mt: 0.5, mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Forma de Pagamento</InputLabel>
                    <Select
                      value={formData.payment_type}
                      label="Forma de Pagamento"
                      onChange={(e) => setFormData({ ...formData, payment_type: e.target.value })}
                    >
                      <MenuItem value="01">Dinheiro</MenuItem>
                      <MenuItem value="02">Cheque</MenuItem>
                      <MenuItem value="03">Cartão de Crédito</MenuItem>
                      <MenuItem value="04">Cartão de Débito</MenuItem>
                      <MenuItem value="05">Crédito Loja</MenuItem>
                      <MenuItem value="17">PIX</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Valor Total"
                    value={formData.total_value.toFixed(2)}
                    disabled
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setOpenEmit(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleEmit}
            disabled={emitting || !formData.items[0]?.descricao}
          >
            {emitting ? <CircularProgress size={24} /> : `Emitir ${emitType.toUpperCase()}`}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openCancelDialog}
        onClose={handleCloseCancelDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cancelar NF-e</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Informe a justificativa do cancelamento (mínimo 15 caracteres).
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={4}
            label="Justificativa"
            value={cancelJustification}
            onChange={(e) => setCancelJustification(e.target.value)}
            helperText={`${cancelJustification.trim().length}/15 caracteres mínimos`}
            error={cancelJustification.trim().length > 0 && cancelJustification.trim().length < 15}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseCancelDialog} disabled={cancelling}>
            Fechar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleCancel}
            disabled={cancelling || cancelJustification.trim().length < 15}
          >
            {cancelling ? <CircularProgress size={20} /> : 'Confirmar Cancelamento'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
